import * as angular from "angular";
import * as d3 from "d3";
import * as _ from "lodash";
import chroma from "chroma-js";
import { DashboardWidget } from "../../models/dashboard-widget.model";

export class ChartHelper {
    private __env: any;
    private getStartDate?: any;
    public chartColors?: any;
    public gender = ["#7a539d", "#a487b9"];
    public age = ["#ac2158", "#D64B83", "#E17CA6"];

    public teal = [
        "#008bb1",
        "#00a2ca",
        "#00b3e6",
        "#1accff",
        "#4dd8ff",
        "#80e3ff",
        "#b3eeff",
        "#e5f9ff",
    ];
    public purple = [
        "#7A539D",
        "#9a72b7",
        "#B88FCE",
        "#CCA3DE",
        "#E1B7EE",
        "#F5CBFE",
    ];
    public green = [
        "#3f652d",
        "#4c792d",
        "#5b912d",
        "#69a62a",
        "#7cb82f",
        "#92c749",
        "#afd775",
        "#cee4a2",
        "#e4efc7",
    ];
    public red = [
        "#98041b",
        "#c9342e",
        "#ef6c5a",
        "#f38a78",
        "#f7a897",
        "#fec9b8",
        "#ffe2d2",
    ];
    public aqua = [
        "#117988",
        "#15858f",
        "#219aa0",
        "#01aeb3",
        "#57bfc1",
        "#80ced0",
        "#a9dddd",
        "#d2ebeb",
    ];
    public pink = [
        "#ac2158",
        "#BF366D",
        "#D64B83",
        "#E17CA6",
        "#d68bab",
        "#f7c8de",
        "#fbe2ed",
    ];
    public grey = [
        "#535559",
        "#686a6c",
        "#86888a",
        "#a4a6a7",
        "#9d9fa0",
        "#b2b5b6",
        "#caccce",
        "##e0e2e4",
    ];
    public orange = [
        "#933304",
        "#bd5c13",
        "#d1711b",
        "#e68523",
        "#ec9f48",
        "#f3b86d",
        "#f9d291",
        "#ffebb6",
    ];
    public blue = ["#87d2f0", "#7ec8e6", "#53b5df", "#28a3d7", "#2095bf"];

    public palettes = [
        {
            key: "green_red_blue",
            name: "Green - Blue - Red",
            colors: ["#00876c", "#5d7cd3", "#de425a"],
        },
        {
            key: "blue_red",
            name: "Green - Blue - Red",
            colors: ["#2e84c5", "#c23726"],
        },
        {
            key: "orange",
            name: "Orange Scale",
            colors: ["#f3c027", "#f68415", "#e8340f", "#c0557a"],
        },
        {
            key: "green_yellow",
            name: "Green - Yellow",
            colors: ["#499081", "#e4e29d", "#e28549", "#de561f"],
        },
        {
            key: "pink_navy",
            name: "Pink - Navy",
            colors: ["#ffc6d2", "#e64bac", "#031066"],
        },
        {
            key: "black_rust",
            name: "Black - Rust",
            colors: ["#2e2e2e", "#e58534", "#dc545b"],
        },
        {
            key: "light_pastel",
            name: "Light Pastel",
            colors: ["#e2f5bd", "#fed693", "#bbe4d1"],
        },
        {
            key: "dark_green",
            name: "Dark Green",
            colors: ["#580003", "#3b9752", "#09403e"],
        },
    ];

    paletteFromKey(key: string): any {
        angular.forEach(this.palettes, (palette) => {
            if (palette.key == key) {
                return palette;
            }
        });
        return null;
    }

    public multiOne = chroma
        .scale(["#ffc6d2", "#e64bac", "#031066"])
        .mode("lch")
        .colors(5);

    generatePalette(colors: string[], numColors: number): string[] {
        return chroma.scale(colors).mode("lch").colors(numColors);
    }

    public colorPalettes = [
        this.teal,
        this.purple,
        this.pink,
        this.green,
        this.red,
        this.aqua,
        this.orange,
        this.grey,
    ];

    public lineOptions: any = {
        chart: {
            type: "multiChart",
            height: 300,
            noData: "No data found for your selected filters",
            margin: {
                top: 30,
                right: 50,
                bottom: 50,
                left: 50,
            },
            yAxis: {
                tickFormat: (d) => {
                    return d3.format(", .2r")(d);
                },
                tickPadding: 15,
            },
            xAxis: {
                tickFormat: (d) => {
                    console.log(d);
                    return d;
                },
                tickPadding: 15,
            },
            // color: ["#169265", "#52bed6", "#eeaf27"],
        },
    };

    shouldRotateLabels(data: any[], chartWidth: number): boolean {
        if (!data || data.length === 0) return false;

        // Calculate average label length
        const avgLabelLength =
            data.reduce((sum, item) => {
                const label = item.label || "";
                return sum + label.toString().length;
            }, 0) / data.length;

        // Estimate space available per label
        const spacePerLabel = chartWidth / data.length;

        // CSS style is 13px but using 9 pixels per character as an average
        const estimatedLabelWidth = avgLabelLength * 9;

        // Calculate if labels overlap. Using 80% of space to leave a buffer
        return estimatedLabelWidth > spacePerLabel * 0.8;
    }

    buildChartOptions(
        widget: DashboardWidget,
        controlsStateChanged?: any,
        legendStateChanged?: any
    ) {
        const options = {
            chart: {
                type: this.getNvChartType(widget),
                height: 300,
                cornerRadius: 6,
                padAngle:
                    widget.style && widget.style.chartType == "donut"
                        ? 0.02
                        : 0,
                pullOutRadius: 0,
                noData: "No data found for your selected filters",
                staggerLabels: false,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 50,
                    left: 65,
                },
                legendPosition: "right",
                donut: widget.style && widget.style.chartType == "donut",
                yAxis: {
                    tickFormat: (d) => {
                        return d3.format(", .2r")(d);
                    },
                    tickPadding: 15,
                },
                legend: {
                    dispatch: {
                        legendClick: (e: any): void => {
                            console.log(e);
                        },
                    },
                    position: "right",
                },
                controls: {
                    dispatch: {
                        legendClick: (e: any): void => {
                            controlsStateChanged(e);
                        },
                    },
                },
                labelType: function (d) {
                    let label =
                        d.data.label.length > 14
                            ? d.data.label.substring(0, 12) + "... "
                            : d.data.label;

                    let figure =
                        widget.style.chartOptions.labelFigure == "count"
                            ? d.data.count.toLocaleString()
                            : d.data.percent.toFixed(1) + "%";

                    return label + ": " + figure;
                },
            },
        };

        if (this.getNvChartType(widget) == "multiBarChart") {
            options.chart.margin = {
                top: 30,
                right: 30,
                bottom: 60,
                left: 55,
            };
        }

        if (
            widget.style &&
            (widget.style.chartType == "donut" ||
                widget.style.chartType == "pie")
        ) {
            options.chart.margin = {
                top: 30,
                right: 50,
                bottom: 50,
                left: 50,
            };

            options.chart["tooltip"] = {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            };

            // options.chart["showLabels"] = false;
            options.chart["showLegend"] = false;
            options.chart["labelsOutside"] = true;
            options.chart["labelSunbeamLayout"] = false;
            options.chart["labelThreshold"] = 0.05;
        }

        if (widget.style.chartOptions) {
            options.chart["stacked"] = widget.style.chartOptions.stacked;
        }

        return options;
    }

    getNvChartType(widget: DashboardWidget) {
        if (widget.style.chartType == "bar") {
            if (widget.overtime) {
                return "multiBarChart";
            } else {
                return "discreteBarChart";
            }
        } else if (widget.style.chartType == "area") {
            return "stackedAreaChart";
        } else if (widget.style.chartType == "donut") {
            return "pieChart";
        }
        return widget.style.chartType + "Chart";
    }

    setDateAxis(chart: any, data: any): void {
        chart.xAxis = {
            tickFormat: (d: number): string => {
                // Linear scales emit fractional/out-of-range ticks on small
                // domains — only integer ticks map to a bucket.
                const point = data[0] && data[0].values ? data[0].values[d] : null;
                return point ? point.date : "";
            },
            tickPadding: 15,
        };
    }

    public multiLineOptions = {
        chart: {
            type: "multiChart",
            height: 300,
            noData: "No data found for your selected filters",
            margin: {
                top: 30,
                right: 50,
                bottom: 50,
                left: 50,
            },
            // useInteractiveGuideline: true,
            yAxis1: {
                tickFormat: (d) => {
                    return d3.format("d")(d);
                },
                tickPadding: 15,
            },
            yAxis2: {
                tickFormat: (d) => {
                    return d3.format("d" + " &starf;")(d);
                },
                tickPadding: 10,
            },
            tooltip: {
                contentGenerator: (d) => {
                    let series = d.series[0];
                    if (series.value === null) return;

                    let date = this.getStartDate();
                    date = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate() + (d.point.x - 1)
                    );

                    let key = series.key;
                    if (series.color == "#ecb581cf") {
                        key = d.data[2].originalKey; //MASSIVE HACK: Can't find any way around it. Need to not show "(right index)" in tooltip
                    }

                    let rows =
                        "<tr>" +
                        "<td  class='key'><span style='margin-left: 23px; margin-top: 2px;'>" +
                        key +
                        ": " +
                        parseFloat(series.value).toLocaleString("en", {
                            maximumFractionDigits: 1,
                        }) +
                        "</span></td>" +
                        "</tr>";

                    let header =
                        "<thead>" +
                        "<tr>" +
                        "<td class='legend-color-guide'><div style='background-color: " +
                        series.color +
                        "; display: inline-block;'></div>" +
                        " <strong style='margin-left: 5px'>" +
                        this.formatDate(date) +
                        "</strong></td>" +
                        "<td class='key'></td>" +
                        "</tr>" +
                        "</thead>";

                    return (
                        "<table>" +
                        header +
                        "<tbody>" +
                        rows +
                        "</tbody>" +
                        "</table>"
                    );
                },
            },
            yDomain2: [0, 5],
            color: ["#007fa3", "#a5d8d8", "#ffcf53"],
        },
    };

    public scatterOptions = {
        chart: {
            type: "scatterChart",
            height: 300,
            showLabels: false,
            showLegend: false,
            useInteractiveGuideline: true,
            interactiveUpdateDelay: 300,
            noData: "No data found for your selected filters",
            margin: {
                top: 20,
                right: 40,
                bottom: 20,
                left: 40,
            },
            x: (d) => {
                return parseInt(d.label);
            },
            y: (d) => {
                return parseInt(d.count);
            },
            tooltip: {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            },
        },
    };

    createTooltipContent(d) {
        let series = d.series[0];
        if (series.value === null) return;

        let header =
            "<thead>" +
            "<td class='legend-color-guide'><div style='background-color: " +
            series.color +
            "; display: inline-block;'></div>" +
            " <span style='margin-left: 5px'>" +
            series.key +
            "</span> - <strong>" +
            series.value +
            " (" +
            d.data.percent.toFixed(1) +
            "%)</strong></td>" +
            "<td class='key'></td>" +
            "</tr>" +
            "</thead>";

        return "<table>" + header + "</table>";
    }

    public barOptions = {
        chart: {
            type: "discreteBarChart",
            height: 300,
            showLabels: true,
            showLegend: false,
            noData: "No data found for your selected filters",
            xAxis: {
                tickPadding: 10,
            },
            margin: {
                top: 20,
                right: 10,
                bottom: 30,
                left: 50,
            },
            x: (d) => {
                return d.label;
            },
            y: (d) => {
                return parseInt(d.count);
            },
            yAxis: {
                tickFormat: d3.format(",f"),
            },
            tooltip: {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            },
        },
    };

    public barOptionsNoLabel = {
        chart: {
            type: "discreteBarChart",
            height: 300,
            showLabels: true,
            showLegend: false,
            staggerLabels: true,
            transitionDuration: 300,
            noData: "No data found for your selected filters",
            xAxis: {
                tickPadding: 10,
            },
            margin: { top: 20, right: 0, bottom: 50, left: 50 },
            autoMargins: true,
            x: (d) => {
                return d.label;
            },
            y: (d) => {
                return parseInt(d.count);
            },
            yAxis: {
                tickFormat: d3.format(",f"),
            },
            tooltip: {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            },
        },
    };

    public pieOptions = {
        chart: {
            type: "pieChart",
            height: 204,
            showLabels: false,
            showLegend: false,
            autoMargins: true,
            pullOutRadius: 0,
            noData: "No data found for your selected filters",
            x: (d) => {
                return d.label;
            },
            y: (d) => {
                return d.count;
            },
            tooltip: {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            },
        },
    };

    public donutOptions = {
        chart: {
            type: "pieChart",
            height: 190,
            showLabels: false,
            showLegend: false,
            autoMargins: false,
            margin: { top: 20, right: 0, bottom: 0, left: 0 },
            noData: "No data found for your selected filters",
            donut: true,
            cornerRadius: 6,
            padAngle: 0.02,
            pullOutRadius: 0,
            x: (d) => {
                return d.label;
            },
            y: (d) => {
                return d.count;
            },
            tooltip: {
                contentGenerator: (d) => {
                    return this.createTooltipContent(d);
                },
            },
        },
    };

    personPlural(num) {
        if (num == 1) {
            return "contacts";
        }
        return "contact";
    }

    calculateChartDetails(data, colors?, ignoreColor?) {
        colors = colors
            ? this.generatePalette(colors, data.length)
            : _.cloneDeep(this.teal);
        let total = data.reduce((total, obj) => {
            return total + parseInt(obj.y);
        }, 0);

        angular.forEach(data, (value, index) => {
            // value.color = value.color ?? colors[index % colors.length];
            value.label = value.x;
            value.count = parseInt(value.y);
            value.percent = (parseInt(value.count) / total) * 100;
        });

        return total;
    }

    calculateSurveyChartDetails(data: any[], colors?: string[]): any[] {
        colors = colors ? colors : _.cloneDeep(this.teal);
        let total = data.reduce((total, obj) => {
            return total + parseInt(obj.count);
        }, 0);

        angular.forEach(data, (value, index: number): void => {
            value.color = colors[index % colors.length];
            value.percent = (parseInt(value.count) / total) * 100;
        });

        return total;
    }

    formatDate(date) {
        let monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        let day = date.getDate();
        let monthIndex = date.getMonth();

        return day + " " + monthNames[monthIndex];
    }
}

angular
    .module("result", ["angular-inview"])
    .service("ChartHelper", ChartHelper);
